import { useMemo, useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import { reportsApi, type InvoiceRegisterDto } from '../../api/reports';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';

/**
 * D6 — SĄSKAITŲ REGISTRAS: FR0671 (gautos SF) / FR0672 (išrašytos SF) —
 * legacy InvoiceRegisterFR0671_2/FR0672_2 portas. Eilutė per SF: data,
 * numeris, suma, PVM, [virtualus PVM — tik FR0672], kontrahentas (kodas /
 * PVM kodas be šalies priedo / pavadinimas). Langas pagal žurnalo įrašo
 * datą; bet koks rėžis. Pačiai formai VMI naudoti i.SAF eksportą
 * (Ataskaitos → SAF-T / i.SAF).
 */
export default function InvoiceRegister() {
  const [type, setType] = useState<'received' | 'made'>('received');
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<InvoiceRegisterDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await reportsApi.invoiceRegister({ type, from, to }));
    } catch (e: any) {
      setError(e.message || 'Nepavyko sugeneruoti registro');
    } finally {
      setLoading(false);
    }
  };

  const handleCsv = async () => {
    try {
      await reportsApi.invoiceRegisterCsv({ type, from, to });
    } catch (e: any) {
      setError(e.message || 'Eksportas nepavyko');
    }
  };

  const isMade = type === 'made';
  const recordCount = data?.rows.length ?? 0;
  const screenPreview = useMemo(
    () => (data?.rows ?? []).slice(0, 10).map(r => `${r.date} ${r.number} ${r.personName}`.substring(0, 120)),
    [data],
  );

  useReportScreen(
    `Sąskaitų registras (${isMade ? 'FR0672' : 'FR0671'}): ${recordCount} SF ${from}…${to}`,
    { recordCount, filters: { tipas: type, nuo: from, iki: to }, preview: screenPreview },
  );

  return (
    <div className="space-y-4">
      <ReportsNav />
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Sąskaitų registras</h1>
          <p className="text-sm text-gray-500">
            {isMade ? 'FR0672 — išrašytos SF' : 'FR0671 — gautos SF'}; langas pagal žurnalo įrašo datą.
            Formai VMI teikti naudokite i.SAF eksportą.
          </p>
        </div>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label>
          Tipas:{' '}
          <select value={type} onChange={e => setType(e.target.value as 'received' | 'made')}>
            <option value="received">FR0671 — gautos SF</option>
            <option value="made">FR0672 — išrašytos SF</option>
          </select>
        </label>
        <label>
          Nuo:{' '}
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label>
          Iki:{' '}
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <button onClick={handleGenerate} disabled={loading}>
          {loading ? <LoadingSpinner /> : 'Generuoti'}
        </button>
        {data && (
          <button className="secondary" onClick={handleCsv}>CSV</button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {data && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Data</th>
                <th>Numeris</th>
                <th style={{ textAlign: 'right' }}>Suma</th>
                <th style={{ textAlign: 'right' }}>PVM</th>
                {isMade && <th style={{ textAlign: 'right' }}>Virtualus PVM</th>}
                <th>Kontrahento kodas</th>
                <th>PVM kodas</th>
                <th>Pavadinimas</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.rowNumber}>
                  <td>{r.rowNumber}</td>
                  <td>{r.date.slice(0, 10)}</td>
                  <td>{r.number}</td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={r.sum} /></td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={r.sumVat} /></td>
                  {isMade && <td style={{ textAlign: 'right' }}><MoneyCell value={r.virtualVat} /></td>}
                  <td>{r.personCode || '—'}</td>
                  <td>{r.personVat || '—'}</td>
                  <td>{r.personName}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={3}><strong>Viso ({data.invoiceCount} SF)</strong></td>
                <td style={{ textAlign: 'right' }}><strong><MoneyCell value={data.totalSum} /></strong></td>
                <td style={{ textAlign: 'right' }}><strong><MoneyCell value={data.totalVat} /></strong></td>
                {isMade && <td style={{ textAlign: 'right' }}><strong><MoneyCell value={data.totalVirtualVat} /></strong></td>}
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
